export type EngineSetupFlowInput = {
  engineInstalled: boolean;
  installerDownloaded: boolean;
  installerAcknowledged: boolean;
  billingActive: boolean;
  activeOrganizationId: string | null | undefined;
  engineHandoffReady: boolean;
};

export type EngineSetupFlow = {
  installerAcknowledgedOnly: boolean;
  effectiveInstallerAcknowledged: boolean;
  installDetail: string;
  installAction: string;
  installStatusOnly: boolean;
  pairDetail: string;
  pairAction: string;
  pairDisabled: boolean;
  pairDisabledByInstallDetection: boolean;
  acknowledgementNote: string | null;
};

export function describeEngineSetupFlow({
  engineInstalled,
  installerDownloaded,
  installerAcknowledged,
  billingActive,
  activeOrganizationId,
  engineHandoffReady,
}: EngineSetupFlowInput): EngineSetupFlow {
  const installerAcknowledgedOnly = !engineInstalled && installerAcknowledged;
  const effectiveInstallerAcknowledged = engineInstalled || installerAcknowledged;
  const pairDisabled = !billingActive || !activeOrganizationId || !effectiveInstallerAcknowledged || engineHandoffReady;

  return {
    installerAcknowledgedOnly,
    effectiveInstallerAcknowledged,
    installDetail: engineInstalled
      ? 'Engine has reported back as installed.'
      : installerAcknowledgedOnly
        ? 'Installation acknowledged locally in this browser; Engine has not reported back yet. Detection stays pending until Engine opens and checks in. Pair this Mac next.'
        : installerDownloaded
          ? 'Downloaded. Open the installer on this Mac, finish the install, then click “I’ve installed it”. You can download again if needed.'
          : '',
    installAction: engineInstalled ? 'Installed' : installerAcknowledgedOnly ? 'Acknowledged locally' : installerDownloaded ? 'I’ve installed it' : '',
    installStatusOnly: engineInstalled || installerAcknowledgedOnly,
    pairDetail: !effectiveInstallerAcknowledged
      ? 'Install the Engine app you downloaded first, then pair it with your Oracord profile.'
      : installerAcknowledgedOnly
        ? 'Installation is only acknowledged in this browser until Engine reports back. Pair this Mac now so Engine can receive the secure handoff and update detection.'
        : 'Click once to create the secure pairing handoff and open Oracord Engine on this Mac.',
    pairAction: engineHandoffReady
      ? 'Paired'
      : !billingActive
        ? 'Choose plan first'
        : !effectiveInstallerAcknowledged
          ? 'Install Engine first'
          : 'Pair this Mac',
    pairDisabled,
    pairDisabledByInstallDetection: !engineInstalled && installerAcknowledged && billingActive && Boolean(activeOrganizationId) && !engineHandoffReady && pairDisabled,
    acknowledgementNote: installerAcknowledgedOnly
      ? 'Installed is user-acknowledged on this browser until Engine reports back; detection may still show Not installed.'
      : null,
  };
}
